import { useIntl } from 'react-intl';
import { ButtonUI, Tooltip, TooltipInteractionEnum } from 'sonata-react-components';

import { HoverTooltip } from '@/components/common/HoverTooltip/HoverTooltip';
import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import { formatAdTypeLabels, hasAudioAdType } from '../deliveryDrivers.helpers';

import type { DoohReportScreen } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './screenTechSpecs.scss';

type Props = {
  id: string;
  screen: DoohReportScreen;
};

/**
 * Eye icon (same button as the events' one) opening on click the screen's tooltip: its tech specs (resolution, ad format and spot
 * length) and its performance (OTA, impressions, playouts and on-target share).
 */
export default function ScreenTechSpecs({ id, screen }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatCount, formatOta, formatPercent, notAvailable } = useDoohReportFormatters();
  const { techSpecs } = screen;

  const formatLabel = (): string => {
    if (techSpecs.allowedAdTypes.length === 0) {
      return notAvailable;
    }
    const label = formatAdTypeLabels(techSpecs.allowedAdTypes).join(' / ');
    return hasAudioAdType(techSpecs.allowedAdTypes)
      ? label
      : formatMessage(
          { defaultMessage: '{label} (No audio)', id: 'doohReport.deliveryDrivers.techSpecs.formatNoAudio' },
          { label }
        );
  };

  const title = formatMessage({
    defaultMessage: "Screens' Tech Specs",
    id: 'doohReport.deliveryDrivers.techSpecs.title'
  });

  const specs = (
    <div className="screenTechSpecs">
      <div className="screenTechSpecs__header">
        <i className="fa-kit fa-screen screenTechSpecs__headerIcon" />
        {title}
      </div>
      <div className="screenTechSpecs__row">
        <span className="screenTechSpecs__label">
          {formatMessage({ defaultMessage: 'Resolution', id: 'doohReport.deliveryDrivers.techSpecs.resolution' })}
        </span>
        <span className="screenTechSpecs__value">{techSpecs.resolution ?? notAvailable}</span>
      </div>
      <div className="screenTechSpecs__row">
        <span className="screenTechSpecs__label">
          {formatMessage({ defaultMessage: 'Format', id: 'doohReport.deliveryDrivers.techSpecs.format' })}
        </span>
        <span className="screenTechSpecs__value">{formatLabel()}</span>
      </div>
      <div className="screenTechSpecs__row">
        <span className="screenTechSpecs__label">
          {formatMessage({ defaultMessage: 'Spot', id: 'doohReport.deliveryDrivers.techSpecs.spot' })}
        </span>
        <span className="screenTechSpecs__value">
          {techSpecs.spotLengthSeconds === null
            ? notAvailable
            : formatMessage(
                { defaultMessage: '{seconds}s', id: 'doohReport.deliveryDrivers.spotLength' },
                { seconds: techSpecs.spotLengthSeconds }
              )}
        </span>
      </div>
      <div className="screenTechSpecs__header">
        <i className="fa-solid fa-chart-simple screenTechSpecs__headerIcon" />
        {formatMessage({ defaultMessage: 'Performance', id: 'doohReport.deliveryDrivers.techSpecs.performance' })}
      </div>
      {[
        {
          key: 'ota',
          label: formatMessage({ defaultMessage: 'OTA', id: 'doohReport.deliveryDrivers.techSpecs.ota' }),
          value: formatOta(screen.ota)
        },
        {
          key: 'impressions',
          label: formatMessage({
            defaultMessage: 'Impressions',
            id: 'doohReport.deliveryDrivers.techSpecs.impressions'
          }),
          value: formatCount(screen.impressions)
        },
        {
          key: 'playouts',
          label: formatMessage({ defaultMessage: 'Playouts', id: 'doohReport.deliveryDrivers.techSpecs.playouts' }),
          value: formatCount(screen.playouts)
        },
        {
          key: 'onTarget',
          label: formatMessage({
            defaultMessage: '% On target',
            id: 'doohReport.deliveryDrivers.techSpecs.onTarget'
          }),
          value: formatPercent(screen.onTargetPct)
        }
      ].map(row => (
        <div className="screenTechSpecs__row" key={row.key}>
          <span className="screenTechSpecs__label">{row.label}</span>
          <span className="screenTechSpecs__value">{row.value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <HoverTooltip content={title}>
      <span>
        <Tooltip
          config={{
            arrow: true,
            component: specs,
            interactions: [TooltipInteractionEnum.click],
            placement: 'bottom-end',
            portal: true,
            strategy: 'fixed'
          }}
        >
          <ButtonUI
            className="screenTechSpecs__trigger"
            icon="fa-regular fa-eye"
            id={`${id}-trigger`}
            shape="link"
            size="small"
            type="secondary"
          />
        </Tooltip>
      </span>
    </HoverTooltip>
  );
}
